/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Carousel, Input, Button, Spin, Checkbox, notification,
  Form, Row, Col, Modal,
} from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import {
  get, isSuperAgentError, post, ResponseWrapper, RequestWrapper,
} from '../../../dataSource/requests';
import {
  GENERIC_ERROR_MESSAGE,
  USER_REGISTRATION_MESSAGES,
} from '../../../utils/responseMessages';
import './RegisterComponent.scss';
import { getDataUserSAP } from '../../../utils/sapUtils';
import { namespaces } from '../../../i18n/i18n.constants';
import TokenUserValidationComponent from '../TokenUserValidationComponent/TokenUserValidationComponent';

interface AcceptanceFile {
  id: number;
  name: string;
  registrationDate: string;
  size: number;
  type: string;
  typeUser: string;
  version: string;
}

interface AcceptanceDocuments {
  file: AcceptanceFile;
  id: number;
  name: string;
}

const RegisterComponent = (): JSX.Element => {
  const { t } = useTranslation('namespaces');
  const [formInstanceFederalRegistration] = Form.useForm();
  const [formInstancePasswordAndTerms] = Form.useForm();
  const [formInstanceToken] = Form.useForm();
  const navigate = useNavigate();
  const elementCarouselReference = useRef(null);

  const sapUserData2 = useRef({});
  const responseCodeToken = useRef<number>(0);
  const [, setSapDataLoading] = useState<boolean>(false);
  const [federalRegistrationLoading, setFederalRegistrationLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [handleSubmitLoading, setHandleSubmitLoading] = useState<boolean>(false);
  const [termsLoading, setTermsLoading] = useState<boolean>(false);
  const [acceptanceDocuments, setAcceptanceDocuments] = useState<Array<AcceptanceDocuments>>([]);

  useEffect(() => {
    const executeOnLoad = async (): Promise<void> => {
      let responseFile: ResponseWrapper | undefined;
      let responseFileError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/file/getTermsOfAcceptance',

        noAuth: true,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });
      try {
        responseFile = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseFileError = error.response;
        } else {
          genericError = error as Error;
        }
        console.log('responseFileError', responseFileError);
      } finally {
        setTermsLoading(false);
      }

      switch (true) {
        case (responseFile?.body?.code === 9012):
          setAcceptanceDocuments(responseFile?.body.terms);
          break;

        case (responseFileError?.body?.code === -9017):
          notification.error({
            message: 'Erro',
            description: 'Termos de aceite não encontrados',
          });
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Não foi possivel fazer o download do arquivo',
          });
          break;
      }
    };

    executeOnLoad();
    return () => {
      setHandleSubmitLoading(false);
    };
  }, []);

  const next = (): void => {
    elementCarouselReference.current.next();
  };

  const previous = (): void => {
    elementCarouselReference.current.prev();
  };

  const downloadPDF = async (id: number, name: string): Promise<void> => {
    setTermsLoading(true);

    let responseFile: ResponseWrapper | undefined;
    let responseFileError: ResponseWrapper | undefined;
    let genericFileError: Error | undefined;

    if (id) {
      const requestReference: RequestWrapper = get({
        url: `/file/downloadTermsOfAcceptance/${id}`,
        noAuth: true,
        responseType: 'blob',

        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        responseFile = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseFileError = error.response;
        } else {
          genericFileError = error as Error;
        }
        console.log('responseFileError | genericFileError', error);
      } finally {
        setTermsLoading(false);
      }
    } else {
      const requestReference: RequestWrapper = get({
        url: '/file/downloadTermsAndConditions',
        noAuth: true,
        responseType: 'blob',

        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });
      try {
        responseFile = await requestReference;
      } catch (error: unknown) {
        if (isSuperAgentError(error)) {
          responseFileError = error.response;
        } else {
          genericFileError = error as Error;
        }
        console.log('responseFileError | genericFileError', error);
      } finally {
        setTermsLoading(false);
      }
    }

    switch (true) {
      case (responseFile?.status === 200): {
        const fileBlob = new Blob([responseFile?.body], { type: 'application/pdf' });

        const fileLink = document.createElement('a');
        const fileURL = window.URL.createObjectURL(fileBlob);

        fileLink.href = fileURL;
        fileLink.download = !name ? 'Termos e Condições' : name;

        document.body.append(fileLink);

        fileLink.click();

        setTimeout(() => {
          fileLink.remove();
          window.URL.revokeObjectURL(fileURL);
        }, 0);
      } break;

      case (responseFileError?.body?.code === -9002):
        notification.error({
          message: 'Erro',
          description: 'Documento não encontrado.',
        });
        break;

      case (responseFileError?.body?.code === -9018):
        notification.error({
          message: 'Erro',
          description: 'O Arquivo não é um termo de aceite',
        });
        break;

      case (responseFile?.body?.code === 8002):
        notification.info({
          message: 'Atenção',
          description: 'Não existem termos de aceite cadastrados',
        });
        break;

      case (genericFileError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseFileError?.body?.code === -4000):
      default:
        notification.warning({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao buscar o documento',
        });
        break;
    }
  };


  const handleBpInfo = (
    params = {
      federalRegistration: '',
      type: 'federalRegistration',
      password: ''
    },
  ): RequestWrapper => {

    const allowedTypes = ['bp', 'federalRegistration'];
  
    if ( !params.federalRegistration) {
      throw new Error('Erro. BP ou documento não informado.');
    }
  
    if (!params?.type) {
      throw new Error('Erro. Tipo de pesquisa não informado.');
    }
  
    if (!allowedTypes.includes(params.type)) {
      throw new Error(`Erro. Tipo inválido: "${params.type}". Tipos de pesquisa disponiveis: ${allowedTypes.join(', ')}.`);
    }
  
    const requestReference: RequestWrapper = post({
      url: '/auth/infoBp/',
      params: params,
      noAuth: true,
      frontConfigs: {
        navigateFunction: navigate,
      },
    });
  
    return requestReference;
    
  };
  


  const handleSapDataSearch = async (): Promise<boolean> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    try {
      setSapDataLoading(true);
      const requestRef: RequestWrapper = handleBpInfo({
        federalRegistration: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
        type: 'federalRegistration',
        password: formInstancePasswordAndTerms.getFieldValue('password')
      });
      

      response = await requestRef;
    } catch (error: unknown) {

      let message;
      if (isSuperAgentError(error)) {
        responseError = error.response;
        message = error.message;
      }

      if (responseError === undefined || responseError?.body.code === -19000 ) {
        sapUserData2.current = {};
        notification.error({
          message: '',
          description: 'SAP indisponível. Tente novamente mais tarde.',
        });

        return false;
      }

      if (responseError?.body.code === -19001) {
        sapUserData2.current = {};
        notification.error({
          message: '',
          description: 'ERRO 500 - Sem comunicação com o SAP - Alto tempo de espera ou servidor está inacessível.',
        });
        return false;
      }
      if (responseError?.body.code === -19002 ) {
        sapUserData2.current = {};
        notification.error({
          message: '',
          description: 'ERRO 404 - Erro em ponto de comunicação',
        });
        return false;
      }
    } finally {
      setSapDataLoading(false);
    }

    switch (true) {
      case (response?.statusCode === 200):
        sapUserData2.current = response?.body;
        return true;

      case (responseError != null):
      default:
        sapUserData2.current = {};
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao buscar os dados do usuário.',
        });
        return false;
    }
  };

  

  const fetchSap = async (): Promise<boolean> => {
    let sapResult;
    let sapResultError;

    try {
      sapResult = await handleSapDataSearch();
    } catch (error: unknown) {
      sapResultError = error;
      console.log('error', error);
    }
    switch (true) {
      case (sapResult):
        return true;

      case (sapResultError != null):
      default: {
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push('Desculpe, houve um erro ao buscar os dados do usuário.');

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } return false;
    }
  };

  const handleSubmit = async (): Promise<void> => {
    let responseSap;
    let responseSapException;
    setHandleSubmitLoading(true);

    const valuesFormFederalRegistration = formInstanceFederalRegistration.getFieldsValue();
    const valuesFormPasswordAndTerms = formInstancePasswordAndTerms.getFieldsValue();
    const params = {
      federalRegistration: valuesFormFederalRegistration.federalRegistration,
      password: valuesFormPasswordAndTerms.password,
    };
  //  setEmail(sapUserData2.current.email.toString().toLowerCase());

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;
    const requestReference: RequestWrapper = post({
      url: '/auth/signup/',
      params,

      noAuth: true,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;



    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
      console.log('responseError', responseError);
    }

    if( response?.body?.code === 1019 || response?.body?.code === 1020 
      || response?.body?.code == 1021 ) {
        try {
          responseSap = await fetchSap();
        } catch (error: unknown) {
          responseSapException = error;
        }

        if (responseSap === false || (responseSapException != null)) {
          return;
        }
    }
      
    switch (true) {
      case (response?.body?.code === 1019):
        // "Token send successfully", 1019
        responseCodeToken.current = 1019;
        next();
        break;

      case (response?.body?.code === 1020):
        // "Telephone token send successfully", 1020
        responseCodeToken.current = 1020;
        next();
        break;

      case (response?.body?.code === 1021):
        // "Email token send successfully", 1021
        responseCodeToken.current = 1021;
        next();
        break;

      case (responseError?.body?.code === -1027):
        // "Federal registration is not valid", -1027
        notification.error({
          message: 'Erro',
          description: 'CPF/CNPJ inválido.',
        });
        break;

      case (responseError?.body?.code === -19000):
        // "SAPTimeOut", -19000
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPTimeOut', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19001):
        // "SAPNotFound", -19001
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPNotFound', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -19002):
        // "SAPBadRequest", -19002
        notification.error({
          message: t('error', { ns: namespaces.common }),
          description: t('SAPBadRequest', { ns: namespaces.error.sap }),
        });
        break;

      case (responseError?.body?.code === -2000):
        // "Matriculation cannot be null", -2000
        notification.error({
          message: 'Erro',
          description: 'Matrícula não pode ser nula.',
        });
        break;

      case (responseError?.body?.code === -2001):
        // "Duplicated matriculations", -2001
        notification.error({
          message: 'Erro',
          description: 'Matrículas duplicadas.',
        });
        break;

      case (responseError?.body?.code === -3000):
        // "Role not found", -3000
        elementCarouselReference.current.goTo(0);

        notification.error({
          message: 'Erro',
          description: USER_REGISTRATION_MESSAGES.ROLE_NOT_FOUND,
        });
        break;

      case (responseError?.body?.code === -1022):
        // "Email and telephone not found in sap database", -1022
        notification.error({
          message: 'Dados inconsistentes!',
          description: 'Campo e-mail e telefone vazio, procure a filial mais próxima para atualizar os seus dados cadastrais.',
        });
        break;

      case (responseError?.body?.code === -1028):
        // "For Cooperated the federal registration must be informed", -1028
        notification.error({
          message: 'Erro',
          description: 'Para Cooperado deve ser informado o CPF/CNPJ.',
        });
        break;

      case (responseError?.body?.code === -2005):
        // There are no valid matriculations, -2005
        notification.error({
          message: 'Erro',
          description: 'Não há matrículas ativas.',
        });
        break;

      case (responseError?.body?.code === -5021):
          notification.error({
            message: 'Erro',
            description: 'Número de telefone maior que 11 digitos.',
          });
          break;
      case (responseError?.body?.code === -5022):
            notification.error({
              message: 'Erro',
              description: 'Número de telefone tem menos digitos que o padrão.',
            });
            break;
      case (responseError?.body?.errors): {
        const errorMessage = responseError?.body.errors[0].defaultMessage;

        notification.error({
          message: 'Erro',
          description: errorMessage,
        });
      } break;

      case (response?.body?.errors): {
        const errorMessage = response?.body.errors[0].defaultMessage;

        notification.error({
          message: 'Erro',
          description: errorMessage,
        });
      } break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.body?.code === -4000):
      default:
        // "Something unexpected happened", -4000
        notification.error({
          message: 'Erro',
          description: GENERIC_ERROR_MESSAGE,
        });
        break;
    }

    setHandleSubmitLoading(false);
  };

  const checkCheckBox = (rule: any): Promise<void> => {
    const values = formInstancePasswordAndTerms.getFieldsValue(['termschecked']);
    let checked = true;

    Object
      .keys(values)
      .forEach((key) => {
        if (key.includes('termschecked')) {
          if (!values[key]) {
            checked = false;
          }
        }
      });

    if (!checked) {
      return rule.field === 'termschecked'
        ? Promise.reject(new Error('Por favor, aceite os Termos e Condições de uso'))
        : Promise.reject();
    }

    return Promise.resolve();
  };

  const compareToFirstPassword = (_rule: any, value: string): Promise<void> => {
    return !value || value !== formInstancePasswordAndTerms.getFieldValue('password')
      ? Promise.reject(new Error('Nova senha e confirmar nova senha devem ser iguais'))
      : Promise.resolve();
  };

  const handleFederalRegistration = async (
    formValues: {
      federalRegistration: string;
    },
  ): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = post({
      url: '/auth/federalRegistrationValidation',

      noAuth: true,

      params: {
        federalRegistration: formValues.federalRegistration,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      setFederalRegistrationLoading(true);
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }

      console.log('error', error);
    } finally {
      setFederalRegistrationLoading(false);
    }

    switch (true) {
      case (response?.body?.code === 1017):
        next();
        break;

      case (responseError?.body?.code === -1017): {
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push('Documento já cadastrado no Campo Digital. Favor seguir com o login.');

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;

      case (responseError?.body?.code === -1019): {
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push('O CPF/CNPJ está vinculado a outro usuário.');

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;

      case (responseError?.body?.code === -1039): {
        Modal.confirm({
          title: 'Atenção',
          content: (
            <Row justify="start">
              <Col span={24} style={{ paddingBottom: 15 }}>
                Estes dados estão vinculados a uma conta que encontra-se sobre análise de pedido de exclusão de dados.
              </Col>
              <Col span={24}>
                É necessário que o término da análise ocorra pela Coopercitrus para que uma nova conta possa ser cadastrada.
              </Col>
            </Row>),
          okText: 'Ok',
          okCancel: false,
          icon: (<WarningOutlined />),
          width: 500,
        });
        break;
      }

      case (responseError?.body?.code === -1021): {
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push('O CPF/CNPJ não foi encontrado.');

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;

      case (responseError?.body?.code === -1020): {
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push('O CPF/CNPJ informado está vinculado a um usuário inativo. Procure a filial Coopercitrus mais próxima.');

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;

      case (responseError?.body?.code === -1029): {
        /*
        "Access to the application is only allowed for cooperative members,
        employees and interns.", -1029
        */
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push('O acesso ao aplicativo é permitido somente para cooperados, funcionários e estagiários.');

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;

      case (responseError?.body?.code === -19000): {
        // "SAP unavailable", -19000
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push(t('SAPTimeOut', { ns: namespaces.pages.login.registerComponent.registerComponent }));

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;
      case (responseError?.body?.code === -19001): {
        // "SAPNotFound", -19001
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push(t('SAPNotFound', { ns: namespaces.error.sap }));

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;
      case (responseError?.body?.code === -19002): {
        // "SAPBadRequest", -19002
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push(t('SAPBadRequest', { ns: namespaces.pages.login.registerComponent.registerComponent }));

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default: {
        const newErrors = formInstanceFederalRegistration.getFieldError('federalRegistration');
        newErrors.push('Desculpe, houve um problema ao consultar o CPF/CNPJ na Coopercitrus.');

        formInstanceFederalRegistration.setFields([
          {
            errors: newErrors,
            name: ['federalRegistration'],
            touched: formInstanceFederalRegistration.isFieldTouched('federalRegistration'),
            value: formInstanceFederalRegistration.getFieldValue('federalRegistration'),
            validating: formInstanceFederalRegistration.isFieldValidating('federalRegistration'),
            warnings: formInstanceFederalRegistration.getFieldWarning('federalRegistration'),
          },
        ]);
      } break;
    }
  };

  const notifySuccess = (tokenValidationSuccessArgs: boolean): void => {
    if (tokenValidationSuccessArgs === false) {
      next();
    }
  };

  return (
    <Carousel
      dots={false}
      ref={elementCarouselReference}
      className="RegisterComponent"
    >
      {/* Carousel Slide Federal Registration */}
      <div>
        <Row>
          <Col
            xs={{ span: 24 }}
          >
            <Form
              scrollToFirstError
              className="signup-form"
              form={formInstanceFederalRegistration}
              onFinish={handleFederalRegistration}
            >
              <Row>
                <Col
                  xs={{ span: 24 }}
                >
                  <Form.Item
                    className="login-form-style"
                    name="federalRegistration"
                    rules={[
                      {
                        required: true,
                        message: 'CPF/CNPJ é obrigatório.',
                      },
                      {
                        validator: (rule, value): Promise<void> => {
                          if (value.length !== 11 && value.length !== 14) {
                            return Promise.reject(new Error('CPF/CNPJ deve ter exatamente 11 ou 14 dígitos'));
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                    getValueFromEvent={(eventt): any => eventt.target.value.replace(/[\D]/g, '')}
                  >
                    <Input
                      maxLength={14}
                      allowClear
                      placeholder="CPF/CNPJ"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row
                gutter={24}
              >
                <Col
                  xs={{ span: 24 }}
                >
                  <Form.Item
                    noStyle
                  >
                    <Button
                      block
                      size="large"
                      type="primary"
                      htmlType="submit"
                      loading={federalRegistrationLoading}
                      className="RegisterComponent__ContinueButton"
                    >
                      CONTINUAR
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>

      {/* Carousel Slide Password and Terms */}
      <div>
        <strong className="form-title">Senha</strong>
        <Form
          scrollToFirstError
          className="signup-form"
          form={formInstancePasswordAndTerms}
          onFinish={
            (): void => {
              handleSubmit();
            }
          }
        >
          <Form.Item
            className="login-form-style"
            name="password"
            rules={[
              {
                required: true,
                message: 'Por favor, insira a senha',
              },
              {
                min: 6,
                message: 'A senha deve ter no mínimo 6 caracteres',
              },
            ]}
          >
            <Input.Password placeholder="Senha" />
          </Form.Item>
          <Form.Item
            className="login-form-style"
            name="confirm"
            rules={[
              {
                required: true,
                message: 'Por favor, insira a senha',
              },
              {
                validator: compareToFirstPassword,
              },
            ]}
          >
            <Input.Password placeholder="Confirmar Senha" />
          </Form.Item>
          <Form.Item
            className="login-form-style"
          >
            <div className="termsRow">
              <Form.Item
                noStyle
                name="termschecked"
                valuePropName="checked"
                rules={[
                  {
                    validator: checkCheckBox,
                  },
                ]}
              >
                <Checkbox />
              </Form.Item>
              Eu li e aceito os
              <Button
                className="terms-hyperlink"
                onClick={
                  (): void => {
                    downloadPDF();
                  }
                }
                disabled={termsLoading}
                style={termsLoading ? { color: 'gray' } : { color: '#4e8203' }}
                type="link"
              >
                Termos e Condições de uso
              </Button>
              <Spin className="terms-spin" spinning={termsLoading} />
            </div>
            {
              acceptanceDocuments.length > 0 && (
                <div>
                  {acceptanceDocuments.map((file) => {
                    return (
                      <div className="termsRow">
                        <Form.Item
                          noStyle
                          name={`termschecked${file.id}`}
                          valuePropName="checked"
                          rules={[
                            { validator: checkCheckBox },
                          ]}
                        >
                          <Checkbox />
                        </Form.Item>
                        Eu li e aceito o termo
                        <Button
                          className="terms-hyperlink"
                          onClick={
                            (): void => {
                              downloadPDF(file.file.id, file.name);
                            }
                          }
                          disabled={termsLoading}
                          style={termsLoading ? { color: 'gray' } : { color: '#4e8203' }}
                          type="link"
                        >
                          {file.name}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </Form.Item>
          <Button
            onClick={
              (): void => {
                formInstancePasswordAndTerms.resetFields();
                previous();
              }
            }
            type="nav-prev"
            ghost
            className="control-button"
          >
            VOLTAR
          </Button>
          <Form.Item
            noStyle
          >
            <Button
              htmlType="submit"
              type="nav-next"
              className="control-button ant-btn-end"
              loading={handleSubmitLoading}
            >
              {handleSubmitLoading ? 'CARREGANDO' : 'FINALIZAR CADASTRO'}
            </Button>
          </Form.Item>
        </Form>
      </div>

      {/* Carousel Slide Token */}
      <div>
        <Row>
          <Col
            xs={{ span: 24 }}
          >
            <Form
              scrollToFirstError
              className="signup-form"
              form={formInstanceToken}
            >
              <Row>
                <Col
                  xs={{ span: 24 }}
                >
                  {
                    Object.keys(sapUserData2.current).length > 0 && responseCodeToken.current > 0
                      ? (
                        <TokenUserValidationComponent
                          targetValidation={{
                            federalRegistration: sapUserData2.current.federalRegistration.toString(),
                            email: sapUserData2.current.email.toString(),
                            phone: sapUserData2.current.telephone.toString(),
                            responseCodeToken: responseCodeToken.current,
                          }}
                          modalToken={notifySuccess}
                        />
                      ) : null
                  }
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>

      {/* Carousel Slide - Final */}
      <div className="box-message">
        <div className="success-message-container">
          <strong className="info">
            {
              `Um e-mail de confirmação foi enviado para ${email}. 
                Verifique sua caixa de entrada`
            }
          </strong>
        </div>
        <Button
          htmlType="button"
          type="default"
          className="control-button"
          onClick={
            (): void => {
              navigate('/');
            }
          }
        >
          FAZER LOGIN
        </Button>
      </div>
    </Carousel>
  );
};

RegisterComponent.propTypes = {
};

RegisterComponent.defaultProps = {};

export default RegisterComponent;
